import { useCallback, useState } from 'react';
import styled from 'styled-components';
import Modal from '../shared/Modal';
import DifficultyExplanation from './DifficultyExplanation';
import { commonStyles } from '../../theme';

const DifficultySelector = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const DifficultyLabel = styled.label`
  display: block;
  margin-bottom: 16px;
  font-weight: 600;
  color: var(--color-text, ${commonStyles.primaryText});
  font-size: 1.1rem;
  line-height: 1.4;
`;

const DifficultySelect = styled.select`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid var(--color-input-border, ${commonStyles.primaryBorder});
  border-radius: 8px;
  background-color: var(--color-input-bg, ${commonStyles.secondaryBackground});
  color: var(--color-input-text, ${commonStyles.primaryText});
  font-size: 1rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  
  .modal-body & {
    padding: 14px 16px;
    margin-bottom: 0;
  }

  &:focus {
    outline: none;
    border-color: var(--color-focus, ${commonStyles.inputBorder});
    box-shadow: 0 0 0 3px rgba(0, 122, 204, 0.1);
  }

  &:hover {
    border-color: var(--color-input-border-hover, #b8bcc0);
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
`;

const StartButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background-color: var(--color-button-primary, #6aaa64);
  color: var(--color-button-primary-text, #ffffff);
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  .form-row & {
    flex-shrink: 0;
    padding: 14px 20px;
    font-size: 1rem;
    margin-top: 0;
    white-space: nowrap;
  }

  &:hover:not(:disabled) {
    background-color: var(--color-button-primary-hover, #5d9556);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(106, 170, 100, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(106, 170, 100, 0.3);
  }

  &:disabled {
    background-color: var(--color-button-disabled, #d3d6da);
    color: var(--color-button-disabled-text, #787c7e);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus {
    outline: 2px solid var(--color-focus, ${commonStyles.inputBorder});
    outline-offset: 2px;
  }
`;

const DifficultyModal = ({ 
  isOpen,
  onDifficultySelect,
  onClose 
}) => {
  const [difficultySelection, setDifficultySelection] = useState('normal');

  const handleDifficultyChange = (e) => {
    setDifficultySelection(e.target.value);
  };

  const handleDifficultySubmit = useCallback(async (e) => {
    e.preventDefault();
    if (difficultySelection) {
      onDifficultySelect(difficultySelection);
    }
  }, [difficultySelection, onDifficultySelect]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Select Difficulty"
      showCloseButton={true}
    >
      <ModalForm onSubmit={handleDifficultySubmit}>
        <FormRow>
          <DifficultySelector>
            <DifficultyLabel htmlFor="difficultySelection">Choose your difficulty level:</DifficultyLabel>
            <DifficultySelect 
              id="difficultySelection" 
              value={difficultySelection} 
              onChange={handleDifficultyChange}
              required
            >
              <option value="easy">EASY</option>
              <option value="normal">NORMAL</option>
              <option value="hard">HARD</option>
              <option value="impossible">IMPOSSIBLE</option>
            </DifficultySelect>
            {difficultySelection && (
              <DifficultyExplanation
                difficulty={difficultySelection}
              />
            )}
          </DifficultySelector>
        </FormRow>
        <FormRow>
          <StartButton type="submit" disabled={!difficultySelection}>
            Start Puzzle
          </StartButton>
        </FormRow>
      </ModalForm>
    </Modal>
  );
};

export default DifficultyModal;